// public-reader/components/PublicHeader.tsx — minimal sticky header.
//
// Brand on the left (links to www.emberhollow.ai). Right-side action:
// "Your bookshelf →" if the visitor already has a bearer in this browser
// (we don't validate the token here; presence is enough to surface the
// link), else "Sign in".

import type { CSSProperties } from "react";
import { getAccessToken } from "../../auth/tokens";

const WWW_URL = "https://www.emberhollow.ai";

export interface PublicHeaderProps {
  /** Accent colour from the scenario theme. */
  accent: string;
  /** Muted text colour. */
  inkMuted: string;
  /** Paper edge for the hairline. */
  paperEdge: string;
  /** Paper colour for the background. */
  paper: string;
}

export default function PublicHeader(props: PublicHeaderProps): JSX.Element {
  // Read once on render. We don't subscribe — if the visitor signs in mid-
  // session in another tab, they'll see the updated CTA on their next
  // navigation. The header isn't load-bearing for auth.
  const hasToken =
    typeof window !== "undefined" ? Boolean(getAccessToken()) : false;

  const header: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "14px 24px",
    borderBottom: `1px solid ${props.paperEdge}`,
    background: props.paper,
    position: "sticky",
    top: 0,
    zIndex: 4,
    backdropFilter: "blur(6px)",
  };
  const brand: CSSProperties = {
    margin: 0,
    fontFamily: "inherit",
    fontSize: "1rem",
    fontWeight: 500,
    color: props.accent,
    letterSpacing: "0.04em",
    textDecoration: "none",
  };
  const tag: CSSProperties = {
    fontSize: "0.7rem",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: props.inkMuted,
    fontFamily: "'JetBrains Mono', monospace",
    marginLeft: 4,
  };
  const spacer: CSSProperties = { flex: 1 };
  const cta: CSSProperties = {
    color: props.inkMuted,
    textDecoration: "none",
    fontSize: "0.82rem",
    fontFamily: "inherit",
    padding: "6px 12px",
    border: `1px solid ${props.paperEdge}`,
    borderRadius: 2,
    transition: "color 0.18s, border-color 0.18s",
  };

  return (
    <header style={header}>
      <a href={WWW_URL} style={brand} aria-label="Ember Hollow home">
        Ember Hollow
      </a>
      <span style={tag}>Stories</span>
      <div style={spacer} />
      {hasToken ? (
        <a href={WWW_URL} style={cta}>
          Your bookshelf →
        </a>
      ) : (
        <a href={WWW_URL} style={cta}>
          Sign in
        </a>
      )}
    </header>
  );
}
